import React, { useEffect, useMemo } from 'react'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import { Breadcrumbs } from 'components/shared'
import { IBreadcrumbsItem } from 'components/shared/Breadcrumbs'
import SingleTask from 'components/SingleTask'
import { SingleTaskContent } from './TaskView.style'

import { changeActiveTaskAction } from 'store/taskManager/actions'
import { ITask } from 'store/taskManager/model'
import { useClient } from 'hooks/useClient'
import { useTitle } from 'hooks/useTitle'
import { Grid } from '@centra/centra-ui'

import styles from './TaskView.module.scss'

const defaultBreadcrumbs: IBreadcrumbsItem[] = [
  {
    title: '',
    link: '/',
    type: 'client',
  },
  {
    title: '',
    link: '',
    type: 'agency_client',
  },
  {
    title: '',
    link: '',
    type: 'task',
  },
]

function buildTaskNameForBreadcrumb(task: ITask) {
  if (!task) {
    return ''
  }
  return `[${task.id}] ${task.name}`
}

const TaskView = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const {
    params: { id },
  } = useRouteMatch<{ id: string }>()
  const { agency, user, task, taskLoading, clientCompany } = useSelector(({ auth, taskManager }) => ({
    user: auth.user,
    agency: auth.agency,
    clientCompany: auth.clientCompany,
    task: taskManager.task,
    taskLoading: taskManager.taskLoading,
  }))
  const { isAgency } = useClient()
  const breadcrumbs = useMemo<IBreadcrumbsItem[]>(() => {
    if (isAgency && task && agency) {
      const homeItem: IBreadcrumbsItem = {
        title: task.client.agencyName || clientCompany?.name || task?.client.name || '',
        link: '/tasktracker/task',
        type: 'client',
      }

      const clientItem: IBreadcrumbsItem = {
        title: taskLoading ? '' : task.client.name,
        link: `/tasktracker/client/${task.client.id}`,
        type: 'agency_client',
      }

      const taskItem: IBreadcrumbsItem = {
        title: taskLoading ? '' : buildTaskNameForBreadcrumb(task),
        link: `/task/${task.id}`,
        type: 'task',
      }

      return task?.client.kind === 'agency' ? [homeItem, taskItem] : [homeItem, clientItem, taskItem]
    }
    if (!isAgency && task && clientCompany) {
      return [
        {
          title: clientCompany.name,
          link: '/tasktracker/task',
          type: 'client',
        },
        {
          title: taskLoading ? '' : buildTaskNameForBreadcrumb(task),
          link: `/task/${task.id}`,
          type: 'task',
        },
      ]
    }
    return defaultBreadcrumbs
  }, [user, task, agency, clientCompany, taskLoading])

  useEffect(() => {
    dispatch(
      changeActiveTaskAction({ id: Number(id) }, ({ success }) => {
        if (!success) {
          onCloseTask()
        }
      })
    )
  }, [id])

  const onCloseTask = () => {
    history.push('/')
  }

  useTitle('task', [String(task?.id || ''), task?.name || '', task?.client.name || ''])
  return (
    <Grid className={styles.root}>
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <SingleTaskContent>
        <SingleTask onClose={onCloseTask} loading={taskLoading} />
      </SingleTaskContent>
    </Grid>
  )
}

export default TaskView
